import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Avatar, ButtonBase, CircularProgress, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import { Routes } from '../routes/RouteConstants';
import { storage } from '../services/storageService';
import { StorageKeys } from '../services/constants/StorageKeys';
import useLogout from '../features/auth/useLogout';
import { tint } from '../utils/textStyles';

const AVATAR_GREY = '#bdbdbd';

/**
 * The guest's profile menu, the header's last item on every width: a blank avatar over „Profil”
 * that opens
 * „Saját foglalások” and „Bejelentkezés” or „Kijelentkezés”, which used to stand in the bar.
 * `color` is the bar's ink, `paperSx` / `dividerColor` dress the menu as the mobile drawer is.
 *
 * Signing out is a click, never a page load: a page that wrote on load could be set off by any
 * link (docs/SECURITY_RISKS.md, rule 2). The item spins until the server has answered. The local
 * flag goes whatever the answer, and a failed call is reported by useErrorHandler. The remount
 * that follows (`onAuthChange`) brings the header back signed out.
 */
const HeaderProfileMenu = ({ onAuthChange, color, paperSx, sx }) => {
  const navigate = useNavigate();
  const { logout } = useLogout();
  const [anchor, setAnchor] = useState(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const isUserAuthenticated = storage.get(StorageKeys.IS_USER_AUTHENTICATED);
  const close = () => setAnchor(null);

  const handleLogout = async () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);
    await logout();
    storage.set(StorageKeys.IS_USER_AUTHENTICATED, false);
    navigate('/');
    onAuthChange();
  };

  // A login started from the menu is no detour, so it must not end on the page an abandoned
  // earlier login meant to return to, such as a stale /pre-reserve. With no target, the login
  // ends on "Saját foglalások".
  const forgetTargetRoute = () => {
    storage.remove(StorageKeys.TARGET_ROUTE_BEFORE_REDIRECT);
    close();
  };

  const itemSx = { fontSize: 14, fontWeight: 600, py: 1.25, '&:hover': { backgroundColor: tint(color, 0.08) } };
  const iconSx = { color: 'inherit', minWidth: 32 };

  return (
    <>
      <ButtonBase
        onClick={(event) => setAnchor(event.currentTarget)}
        data-testid="header-profile"
        aria-haspopup="menu"
        aria-expanded={!!anchor}
        sx={{
          flexDirection: 'column',
          gap: 0.25,
          px: 1,
          py: 0.5,
          borderRadius: 2,
          color,
          '&:hover, &.Mui-focusVisible': { backgroundColor: tint(color, 0.08) },
          ...sx,
        }}
      >
        {/* Smaller below `md`, where the bar is too (layoutMetrics). */}
        <Avatar sx={{ width: { xs: 30, md: 34 }, height: { xs: 30, md: 34 }, color: '#fff', backgroundColor: AVATAR_GREY }}>
          <PersonIcon sx={{ fontSize: { xs: 21, md: 24 } }} />
        </Avatar>
        <Typography component="span" fontSize={12} fontWeight={500} lineHeight={1.2}>Profil</Typography>
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, minWidth: 210, borderRadius: 2, ...paperSx } }}
      >
        <MenuItem component={NavLink} to={Routes.PROFILE} onClick={close} sx={itemSx}>
          <ListItemIcon sx={iconSx}><EventNoteIcon fontSize="small" /></ListItemIcon>
          Saját foglalások
        </MenuItem>
        {isUserAuthenticated ? (
          <MenuItem onClick={handleLogout} disabled={isLoggingOut} aria-busy={isLoggingOut} sx={itemSx}>
            <ListItemIcon sx={iconSx}>
              {isLoggingOut ? <CircularProgress size={16} color="inherit" /> : <LogoutIcon fontSize="small" />}
            </ListItemIcon>
            Kijelentkezés
          </MenuItem>
        ) : (
          <MenuItem component={NavLink} to={Routes.LOGIN} onClick={forgetTargetRoute} sx={itemSx}>
            <ListItemIcon sx={iconSx}><LoginIcon fontSize="small" /></ListItemIcon>
            Bejelentkezés
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export default HeaderProfileMenu;
